import {
  ChevronDownIcon,
  Cross2Icon,
  Pencil1Icon,
  PlusIcon,
} from "@radix-ui/react-icons";
import { memo, useCallback, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAccountOpportunities } from "@/hooks/api/use-account-opportunities";
import {
  type CustomBuyingGroup,
  useBuyingGroupSelection,
} from "@/hooks/use-buying-group-selection";
import { getStageVariant } from "@/lib/badge-utils";
import { formatDate } from "@/lib/date-formatting";
import { cn } from "@/lib/utils";
import { CustomBuyingGroupModal } from "./custom-buying-group/custom-buying-group-modal";

interface BuyingGroupsSelectorProps {
  accountId?: string;
}

export const BuyingGroupsSelector = memo(function BuyingGroupsSelector({
  accountId,
}: BuyingGroupsSelectorProps) {
  const { data: opportunities, isLoading } = useAccountOpportunities(
    accountId,
    undefined,
    undefined,
    "default", // Always use default mode here to get all opportunities
  );

  const [open, setOpen] = useState(false);
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
  const [groupToEdit, setGroupToEdit] = useState<CustomBuyingGroup | null>(
    null,
  );

  const {
    selectedOpportunityIds,
    isCustomMode,
    customGroups,
    activeCustomGroupId,
    selectOpportunities,
    selectCustomGroup,
    selectAll,
    saveOrUpdateCustomGroup,
    deleteAndResetCustomGroup,
  } = useBuyingGroupSelection();

  const handleOpportunityToggle = (opportunityId: string, checked: boolean) => {
    let newSelection: string[];

    if (checked) {
      newSelection = Array.from(
        new Set([...selectedOpportunityIds, opportunityId]),
      );
    } else {
      newSelection = selectedOpportunityIds.filter(
        (id) => id !== opportunityId,
      );
    }

    selectOpportunities(newSelection);
  };

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      selectAll();
    },
    [selectAll],
  );

  const handleDefineBuyingGroup = () => {
    setGroupToEdit(null);
    setIsCustomModalOpen(true);
    setOpen(false);
  };

  const handleEditCustomGroup = (group: CustomBuyingGroup) => {
    setGroupToEdit(group);
    setIsCustomModalOpen(true);
    setOpen(false);
  };

  const handleSelectCustomGroup = (group: CustomBuyingGroup) => {
    selectCustomGroup(group.id);
    setOpen(false);
  };

  const handleDeleteCustomGroup = (groupId?: string) => {
    const idToDelete = groupId || groupToEdit?.id;
    if (idToDelete) {
      deleteAndResetCustomGroup(idToDelete);
      setGroupToEdit(null);
      setIsCustomModalOpen(false);
    }
  };

  const handleSaveCustomGroup = (
    name: string,
    opportunityIds: string[],
    personIds: string[],
  ) => {
    saveOrUpdateCustomGroup(
      { name, opportunityIds, personIds },
      groupToEdit?.id,
    );
    setIsCustomModalOpen(false);
    setGroupToEdit(null);
  };

  const getDisplayText = () => {
    if (isCustomMode && activeCustomGroupId) {
      const group = customGroups.find((g) => g.id === activeCustomGroupId);
      return group ? `${group.name}` : "Custom Selection";
    }

    if (selectedOpportunityIds.length === 0) {
      return "All";
    }

    if (selectedOpportunityIds.length === 1) {
      const opportunity = opportunities?.find(
        (opp) => opp.id === selectedOpportunityIds[0],
      );
      return opportunity?.name || "1 selected";
    }

    return `${selectedOpportunityIds.length} selected`;
  };

  return (
    <div className="relative">
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="flex items-center justify-between gap-6 min-w-[300px] rounded-lg corner-smooth-lg border border-border bg-white dark:bg-[hsl(195_30%_12%)] text-card-foreground shadow-sm"
            disabled={!accountId}
          >
            <div className="flex gap-2 items-center min-w-0">
              <span className="text-sm font-medium text-muted-foreground shrink-0">
                Buying groups
              </span>
              <span
                className={cn(
                  "truncate",
                  isCustomMode ? "text-success" : "text-primary",
                )}
              >
                {getDisplayText()}
              </span>
            </div>
            <ChevronDownIcon className="h-4 w-4 opacity-50 flex-shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-[400px] max-w-[90vw] max-h-[400px] overflow-y-auto rounded-lg corner-smooth-lg"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
          }}
        >
          <DropdownMenuCheckboxItem
            checked={!isCustomMode && selectedOpportunityIds.length === 0}
            onCheckedChange={() => {
              selectAll();
            }}
            onSelect={(e) => {
              e.preventDefault();
            }}
            className="py-2.5"
          >
            All
          </DropdownMenuCheckboxItem>

          <DropdownMenuSeparator />

          <DropdownMenuLabel>Opportunities</DropdownMenuLabel>

          {isLoading && (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              Loading...
            </div>
          )}

          {!isLoading && opportunities?.length === 0 && (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              No opportunities found
            </div>
          )}

          {!isLoading &&
            opportunities?.map((opportunity) => {
              const hasContacts =
                opportunity.contacts && opportunity.contacts.length > 0;
              const isChecked =
                !isCustomMode &&
                selectedOpportunityIds.includes(opportunity.id);

              return (
                <TooltipProvider key={opportunity.id}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <DropdownMenuCheckboxItem
                          checked={isChecked}
                          onCheckedChange={(checked) =>
                            handleOpportunityToggle(opportunity.id, checked)
                          }
                          className={cn(
                            "py-2.5",
                            !hasContacts &&
                              "text-muted-foreground cursor-default",
                          )}
                          onSelect={(e) => {
                            e.preventDefault();
                          }}
                        >
                          <div className="flex flex-col w-full gap-1.5">
                            <div className="font-medium text-sm">
                              {opportunity.name}
                            </div>
                            <div className="flex flex-wrap gap-2 items-center">
                              <Badge variant="default">
                                {opportunity.type || "N/A"}
                              </Badge>
                              <Badge
                                variant={getStageVariant(
                                  opportunity.is_closed,
                                  opportunity.is_won,
                                )}
                              >
                                {opportunity.stage_name || "N/A"}
                              </Badge>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {opportunity.created_date &&
                                `${formatDate(opportunity.created_date)}${
                                  opportunity.close_date
                                    ? ` - ${formatDate(opportunity.close_date)}`
                                    : ""
                                }`}
                            </div>
                          </div>
                        </DropdownMenuCheckboxItem>
                      </div>
                    </TooltipTrigger>
                    {!hasContacts && (
                      <TooltipContent>
                        <p>No contacts associated with this buying group</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              );
            })}

          <DropdownMenuSeparator />

          <DropdownMenuLabel className="flex justify-between items-center">
            <span>Custom</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-1.5 py-0.5 text-xs"
              onClick={handleDefineBuyingGroup}
            >
              <PlusIcon className="h-3 w-3 mr-1" />
              Add custom buying group
            </Button>
          </DropdownMenuLabel>

          {customGroups.length === 0 ? (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              No custom buying groups defined yet
            </div>
          ) : (
            customGroups.map((group) => (
              <DropdownMenuCheckboxItem
                key={group.id}
                checked={isCustomMode && activeCustomGroupId === group.id}
                onCheckedChange={(checked) => {
                  if (checked) {
                    handleSelectCustomGroup(group);
                  } else if (isCustomMode && activeCustomGroupId === group.id) {
                    selectAll();
                  }
                }}
                className="py-2.5"
                onSelect={(e) => {
                  e.preventDefault();
                }}
              >
                <div className="flex justify-between items-center w-full">
                  <div className="flex-1 truncate pr-2">
                    <div className="font-medium text-sm truncate">
                      {group.name}
                    </div>
                    <div className="text-xs text-muted-foreground flex gap-2 items-center">
                      <span>
                        {group.opportunityIds.length}{" "}
                        {group.opportunityIds.length === 1
                          ? "opportunity"
                          : "opportunities"}
                      </span>
                      <span>•</span>
                      <span>
                        {group.personIds.length}{" "}
                        {group.personIds.length === 1 ? "person" : "people"}
                      </span>
                    </div>
                  </div>
                  <div className="flex">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleEditCustomGroup(group);
                      }}
                    >
                      <Pencil1Icon className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive/90"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDeleteCustomGroup(group.id);
                      }}
                    >
                      <Cross2Icon className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </DropdownMenuCheckboxItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {(selectedOpportunityIds.length > 0 || isCustomMode) && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-8 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
          onClick={handleClear}
        >
          <Cross2Icon className="h-4 w-4 opacity-50 hover:opacity-100" />
        </Button>
      )}

      <CustomBuyingGroupModal
        isOpen={isCustomModalOpen}
        onOpenChange={setIsCustomModalOpen}
        onSave={handleSaveCustomGroup}
        onClear={handleDeleteCustomGroup}
        initialGroup={groupToEdit}
      />
    </div>
  );
});
